import { useState } from 'react';
import {
  Badge,
  Button,
  CasinoIcon,
  LockIcon,
  Panel,
  PriceScanner,
  ProgressBar,
  ResourceIcon,
  RouletteWheel,
  SlotReels,
  Stat,
  StatGrid,
  Tabs,
  ViewHeader,
  WinBurst,
  clamp01,
  formatAmount,
  formatExact,
  type RouletteSector,
} from '@spacegame/ui';
import {
  CASINO_GAME_MIN_LEVEL,
  MIN_STAKE,
  ROULETTE_SECTORS,
  ROULETTE_VOID,
  SLOT_SYMBOLS,
  dailyStakeLimit,
  remainingStake,
  rollPriceIndex,
  sectorOre,
  type HighLowResult,
  type RouletteBet,
  type RouletteResult,
  type SlotResult,
  type SlotSymbol,
} from '@spacegame/engine';
import { useGame, useGameActions } from '../game/GameProvider';

const STAKES = [MIN_STAKE, 250, 1_000, 5_000];

/** Slot symbols are already resource keys (titan/silicate/isotope/aether/chronos) — one visual language everywhere. */
const IDLE_REELS: [SlotSymbol, SlotSymbol, SlotSymbol] = ['titan', 'silicate', 'isotope'];

const ROULETTE_SECTOR_META: RouletteSector[] = Array.from({ length: ROULETTE_SECTORS }, (_, i) => ({
  id: i,
  accent: sectorOre(i) === 'void' ? 'aether' : sectorOre(i) === 'titan' ? 'titan' : 'silicate',
}));

export function CasinoView() {
  const { game, clock } = useGame();
  const actions = useGameActions();
  const [stake, setStake] = useState(STAKES[0]!);
  const [game_, setGame_] = useState<'slots' | 'highlow' | 'roulette'>('slots');

  // Slots
  const [slotSpinning, setSlotSpinning] = useState(false);
  const [slotReels, setSlotReels] = useState<[SlotSymbol, SlotSymbol, SlotSymbol]>(IDLE_REELS);
  const [slotResult, setSlotResult] = useState<SlotResult | null>(null);
  const [slotWinTrigger, setSlotWinTrigger] = useState(0);

  // Roulette
  const [rouletteSpinning, setRouletteSpinning] = useState(false);
  const [rouletteSector, setRouletteSector] = useState<number | null>(null);
  const [rouletteResult, setRouletteResult] = useState<RouletteResult | null>(null);
  const [rouletteWinTrigger, setRouletteWinTrigger] = useState(0);

  // High-Low
  const [priceIndex, setPriceIndex] = useState(() => rollPriceIndex(game.rngSeed).value);
  const [hlSpinning, setHlSpinning] = useState(false);
  const [hlGuess, setHlGuess] = useState<'higher' | 'lower' | null>(null);
  const [hlResult, setHlResult] = useState<HighLowResult | null>(null);
  const [hlWinTrigger, setHlWinTrigger] = useState(0);
  /** True once the result badge should show — separate from `hlSpinning`, which stays true through the post-result hold so the reference doesn't reroll mid-reveal. */
  const [hlRevealed, setHlRevealed] = useState(false);

  // Guards the round-trip window between click and server response, before
  // the reel/wheel/scanner animation (driven by *Spinning) has even started.
  const [submitting, setSubmitting] = useState(false);
  const anySpinning = slotSpinning || rouletteSpinning || hlSpinning || submitting;

  const casinoLevel = game.planets[0]!.buildings.casino;
  const highLowLocked = casinoLevel < CASINO_GAME_MIN_LEVEL.highlow;
  const rouletteLocked = casinoLevel < CASINO_GAME_MIN_LEVEL.roulette;

  const now = clock();
  const limit = dailyStakeLimit(game.level);
  const remaining = remainingStake(game, now);
  const canStake = remaining >= stake && game.resources.credit >= stake && !anySpinning;

  const spinSlots = async () => {
    setSubmitting(true);
    try {
      const outcome = await actions.playSlots(stake);
      const data = outcome?.events.find((e) => e.type === 'casino')?.data as
        | (SlotResult & { stake: number })
        | undefined;
      if (!data) return;
      setSlotResult(data);
      setSlotReels(data.reels);
      setSlotSpinning(true);
    } finally {
      setSubmitting(false);
    }
  };

  const spinRoulette = async (bet: RouletteBet) => {
    setSubmitting(true);
    try {
      const outcome = await actions.playRoulette(stake, bet);
      const data = outcome?.events.find((e) => e.type === 'casino')?.data as
        | (RouletteResult & { stake: number })
        | undefined;
      if (!data) return;
      setRouletteResult(data);
      setRouletteSector(data.sector);
      setRouletteSpinning(true);
    } finally {
      setSubmitting(false);
    }
  };

  const spinHighLow = async (guess: 'higher' | 'lower') => {
    setSubmitting(true);
    try {
      const outcome = await actions.playHighLow(stake, guess, priceIndex);
      const data = outcome?.events.find((e) => e.type === 'casino')?.data as
        | (HighLowResult & { stake: number })
        | undefined;
      if (!data) return;
      setHlGuess(guess);
      setHlResult(data);
      setHlRevealed(false);
      setHlSpinning(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ViewHeader
        eyebrow={`Credit-Sink · Casino Stufe ${casinoLevel} · Tages-Limit nach Account-Level`}
        title="Galaktisches Credit-Casino"
        description={
          highLowLocked || rouletteLocked
            ? `Überschüssige Credits verspielen — oder vermehren. Casino Stufe ${CASINO_GAME_MIN_LEVEL.highlow} schaltet High-Low frei, Stufe ${CASINO_GAME_MIN_LEVEL.roulette} Roulette.`
            : 'Überschüssige Credits verspielen — oder vermehren. Das Haus behält im Schnitt rund 8 %.'
        }
        actions={
          <Tabs
            variant="enclosed"
            value={game_}
            onChange={(id) => {
              if (anySpinning) return;
              setGame_(id as 'slots' | 'highlow' | 'roulette');
            }}
            items={[
              { id: 'slots', label: 'Slots' },
              {
                id: 'highlow',
                label: 'High-Low',
                icon: highLowLocked ? <LockIcon size={12} /> : undefined,
                disabled: highLowLocked,
              },
              {
                id: 'roulette',
                label: 'Roulette',
                icon: rouletteLocked ? <LockIcon size={12} /> : undefined,
                disabled: rouletteLocked,
              },
            ]}
          />
        }
      />

      <StatGrid columns={3}>
        <Stat label="Credits" value={formatAmount(game.resources.credit)} accent="credit" />
        <Stat
          label="Heute verfügbar"
          value={formatAmount(remaining)}
          accent={remaining > 0 ? 'silicate' : 'danger'}
          hint={`Limit ${formatExact(limit)} (Level ${game.level})`}
        />
        <Stat
          label="Bilanz"
          value={formatAmount(game.casino.totalWon - game.casino.totalStaked)}
          accent={game.casino.totalWon >= game.casino.totalStaked ? 'success' : 'danger'}
          hint={`${formatExact(game.casino.totalStaked)} gesetzt`}
        />
      </StatGrid>

      <Panel title="Einsatz" eyebrow="Pro Runde">
        <div className="flex flex-wrap items-center gap-2">
          {STAKES.map((value) => (
            <Button
              key={value}
              size="sm"
              variant={value === stake ? 'primary' : 'secondary'}
              disabled={anySpinning}
              onClick={() => setStake(value)}
            >
              {formatExact(value)}
            </Button>
          ))}
        </div>
        <ProgressBar
          className="mt-3"
          value={clamp01(1 - remaining / Math.max(1, limit))}
          accent={remaining > 0 ? 'credit' : 'danger'}
          label="Heute gesetzt"
          hint={`${formatExact(limit - remaining)} / ${formatExact(limit)}`}
          aria-label="Tages-Limit"
        />
      </Panel>

      {game_ === 'roulette' ? (
        <Panel title="Asteroiden-Roulette" corners>
          <div className="relative flex flex-col items-center gap-4 py-2">
            <WinBurst
              triggerId={rouletteWinTrigger}
              amount={rouletteResult?.payout ?? 0}
              tier={rouletteResult && rouletteResult.payout >= stake * 10 ? 'jackpot' : 'win'}
            />

            <p className="max-w-md text-center text-xs leading-relaxed text-content-muted">
              13 Sektoren: sechs Titan, sechs Silikat und die Leere (0), die beiden
              Erz-Wetten verliert. Erz zahlt 2×, ein einzelner Sektor 12×.
            </p>

            <RouletteWheel
              sectors={ROULETTE_SECTOR_META}
              result={rouletteSector}
              spinning={rouletteSpinning}
              onSettled={() => {
                setRouletteSpinning(false);
                if ((rouletteResult?.payout ?? 0) > 0) setRouletteWinTrigger((n) => n + 1);
              }}
            />

            {!rouletteSpinning && rouletteResult ? (
              <Badge accent={rouletteResult.won ? 'success' : 'danger'}>
                Sektor {rouletteResult.sector} —{' '}
                {rouletteResult.won
                  ? `Treffer! Gewinn: ${formatExact(rouletteResult.payout)} Credits.`
                  : 'verloren.'}
              </Badge>
            ) : null}

            <div className="flex gap-2">
              {(['titan', 'silicate'] as const).map((ore) => (
                <Button
                  key={ore}
                  variant="primary"
                  size="lg"
                  disabled={!canStake}
                  onClick={() => spinRoulette({ kind: 'ore', ore })}
                >
                  <ResourceIcon resource={ore} size={18} />
                  {ore === 'titan' ? 'Titan' : 'Silikat'} — 2×
                </Button>
              ))}
            </div>

            <div className="w-full">
              <div className="mb-2 text-center font-display text-label uppercase text-content-muted">
                Einzelner Sektor — 12×
              </div>
              <div className="flex flex-wrap justify-center gap-1.5">
                {Array.from({ length: ROULETTE_SECTORS }, (_, i) => i).map((s) => {
                  const ore = sectorOre(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      disabled={!canStake}
                      onClick={() => spinRoulette({ kind: 'sector', sector: s })}
                      data-sg-numeric
                      className={[
                        'h-9 w-9 rounded-control border text-xs font-semibold transition-colors',
                        'disabled:pointer-events-none disabled:opacity-40',
                        s === ROULETTE_VOID
                          ? 'border-aether/50 bg-aether/10 text-aether-bright hover:bg-aether/20'
                          : ore === 'titan'
                            ? 'border-titan/45 bg-titan/10 text-titan-bright hover:bg-titan/20'
                            : 'border-silicate/45 bg-silicate/10 text-silicate-bright hover:bg-silicate/20',
                        rouletteSector === s && !rouletteSpinning ? 'ring-1 ring-content' : '',
                      ].join(' ')}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </Panel>
      ) : game_ === 'slots' ? (
        <Panel title="Sci-Fi Slotmachine" corners>
          <div className="relative flex flex-col items-center gap-4 py-2">
            <WinBurst
              triggerId={slotWinTrigger}
              amount={slotResult?.payout ?? 0}
              tier={slotResult?.kind === 'triple' ? 'jackpot' : 'win'}
            />

            <SlotReels
              symbols={SLOT_SYMBOLS}
              result={slotReels}
              spinning={slotSpinning}
              onSettled={() => {
                setSlotSpinning(false);
                if ((slotResult?.payout ?? 0) > 0) setSlotWinTrigger((n) => n + 1);
              }}
            />

            {!slotSpinning && slotResult ? (
              <p
                className={
                  slotResult.payout
                    ? 'text-xs font-semibold text-success'
                    : 'text-xs text-content-muted'
                }
              >
                {slotResult.kind === 'triple'
                  ? `Dreifach ${slotResult.reels[0]}! Gewinn: ${formatExact(slotResult.payout)} Credits.`
                  : slotResult.kind === 'pair'
                    ? `Paar getroffen. Gewinn: ${formatExact(slotResult.payout)} Credits.`
                    : `Kein Gewinn. Einsatz ${formatExact(stake)} Credits verloren.`}
              </p>
            ) : (
              <p className="text-xs text-content-muted">
                Drei gleiche Symbole zahlen am besten. Paare zahlen nur bei Isotop, Aether und Chronos.
              </p>
            )}

            <Button
              variant="premium"
              size="lg"
              icon={<CasinoIcon size={16} />}
              disabled={!canStake}
              onClick={spinSlots}
            >
              {slotSpinning
                ? 'Dreht …'
                : canStake
                  ? `Drehen — ${formatExact(stake)} Cr`
                  : 'Limit oder Credits erschöpft'}
            </Button>
          </div>
        </Panel>
      ) : (
        <Panel title="High-Low · Rohstoffkurse" corners>
          <div className="relative flex flex-col items-center gap-4 py-2">
            <WinBurst triggerId={hlWinTrigger} amount={hlResult?.payout ?? 0} tier="win" />

            <PriceScanner
              current={priceIndex}
              target={hlResult?.next ?? null}
              guess={hlGuess}
              won={hlResult?.won ?? null}
              spinning={hlSpinning}
              onReveal={() => {
                setHlRevealed(true);
                if ((hlResult?.payout ?? 0) > 0) setHlWinTrigger((n) => n + 1);
              }}
              onSettled={() => {
                setHlSpinning(false);
                setHlRevealed(false);
                // Rerolled only now, after the result has had a moment on
                // screen — rerolling at reveal time changed the reference
                // number in the same frame as the win/loss flash, which read
                // as the gauge updating incorrectly rather than a new round.
                setPriceIndex(rollPriceIndex(game.rngSeed + (hlResult?.next ?? 0)).value);
              }}
            />

            {hlRevealed && hlResult ? (
              <Badge accent={hlResult.won ? 'success' : 'danger'}>
                Kurs {hlResult.next} —{' '}
                {hlResult.won
                  ? `richtig getippt! Gewinn: ${formatExact(hlResult.payout)} Credits.`
                  : 'daneben. Einsatz verloren.'}
              </Badge>
            ) : (
              <p className="text-xs text-content-muted">
                Liegt der nächste Kurs höher oder niedriger? Treffer zahlt das 1,84-fache.
              </p>
            )}

            <div className="flex gap-2">
              {(['higher', 'lower'] as const).map((guess) => (
                <Button
                  key={guess}
                  variant={guess === 'higher' ? 'primary' : 'danger'}
                  size="lg"
                  disabled={!canStake}
                  onClick={() => spinHighLow(guess)}
                >
                  {guess === 'higher' ? 'Höher ↑' : 'Niedriger ↓'}
                </Button>
              ))}
            </div>
          </div>
        </Panel>
      )}
    </>
  );
}
